import { useState, useEffect, useRef } from 'react';
import { DashboardConfig, Widget } from '../types/dashboard';
import { getDashboardByName, createDashboard, updateDashboard } from '../services/dashboardService';
import { generateUUID } from '../utils/uuid';

const DASHBOARD_NAME = 'main';

const defaultWidget: Widget = {
  id: generateUUID(),
  deviceId: null,
  deviceName: '',
  type: 'line',
  title: 'Yeni Grafik',
  keys: [],
  keyIds: [],
  timeRange: '1h',
};

export const useDashboardConfig = () => {
  const [config, setConfig] = useState<DashboardConfig>({
    name: DASHBOARD_NAME,
    configJson: { widgets: [{ ...defaultWidget }] },
  });
  const [loading, setLoading] = useState(true);
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const configRef = useRef(config);
  configRef.current = config;
  useEffect(() => {
    getDashboardByName(DASHBOARD_NAME).then(remote => {
      if (remote) {
        let widgets: Widget[] = [];
        try {
          const raw = typeof remote.configJson === 'string'
            ? JSON.parse(remote.configJson as unknown as string)
            : remote.configJson;
          widgets = Array.isArray(raw) ? raw : (raw?.widgets ?? []);
        } catch {
          widgets = [];
        }
        setConfig({ ...remote, configJson: { widgets } });
      }
      setLoading(false);
    });
  }, []);

  const addWidget = (widget: Widget) => {
    const next = { ...configRef.current, configJson: { widgets: [...configRef.current.configJson.widgets, widget] } };
    setConfig(next);
    configRef.current = next;
    setDirtyIds(prev => new Set(prev).add(widget.id));
  };

  const updateWidget = (widget: Widget) => {
    const next = { ...configRef.current, configJson: { widgets: configRef.current.configJson.widgets.map(w => w.id === widget.id ? widget : w) } };
    setConfig(next);
    configRef.current = next;
    setDirtyIds(prev => new Set(prev).add(widget.id));
  };

  const removeWidget = (id: string) => {
    setConfig(prev => ({
      ...prev,
      configJson: { widgets: prev.configJson.widgets.filter(w => w.id !== id) },
    }));
    setDirtyIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  };

  const reorderWidgets = (widgets: Widget[]) => {
    const next = { ...configRef.current, configJson: { widgets } };
    setConfig(next);
    configRef.current = next;
  };

  const persistConfig = async (cfg: DashboardConfig, savedId?: string) => {
    const widgetsToSave = cfg.configJson.widgets.map(({ keys: _keys, ...w }) => w);
    const payload = { name: DASHBOARD_NAME, configJson: JSON.stringify(widgetsToSave) };
    const id = savedId ? Number(savedId) : cfg.id;
    if (id) {
      await updateDashboard({ id: Number(id), ...payload });
      return id;
    } else {
      const created = await createDashboard(payload);
      setConfig(prev => ({ ...prev, id: created.id }));
      return created.id as number;
    }
  };

  const saveWidget = async (widgetId: string) => {
    await persistConfig(configRef.current);
    setDirtyIds(prev => { const s = new Set(prev); s.delete(widgetId); return s; });
  };

  const saveAll = async () => {
    await persistConfig(configRef.current);
    setDirtyIds(new Set());
  };

  const dirty = dirtyIds.size > 0;

  return { config, loading, dirty, dirtyIds, addWidget, updateWidget, removeWidget, reorderWidgets, saveWidget, saveAll };
};
